/**
 * All gap-board colour decisions live here so a palette change is one edit.
 *
 * Every value is taken from the approved Figma "Tags" component (list frame
 * node 4848:8453, detail frame node 4848:8558) and expressed with the
 * semantic tokens from `src/index.css` wherever one resolves to the exact
 * design hex — never a raw Tailwind palette step unless no token matches
 * (see `SHIPPED` below for the one exception).
 */

import type * as React from 'react';
import { AlertCircle, Check, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  GapActionType,
  GapFixStatus,
  GapPriorityBand,
  GapRunRowStatus,
  GapRunSetStatus,
  GapRunVerdict,
  GapSeverityLevel,
  GapStatus,
  GapWorldPatchStatus,
  LadderRung,
} from '../types';
import {
  formatActionLabel,
  formatFixStatusLabel,
  formatRungLabel,
  formatRunRowStatusLabel,
  formatRunSetStatusLabel,
  formatRunVerdictLabel,
  formatSeverityLabel,
  formatStatusLabel,
  formatTicketTestResult,
  formatWorldPatchStatus,
  rungPosition,
  type TicketTestResult,
} from '../utils';

/**
 * Figma's Tags component: 10px/4px padding, Caption (12/16) at regular
 * weight, and a flat fill with NO border and NO leading icon. `Badge`'s own
 * base adds `rounded-full border px-2 py-0.5 font-medium`, so each of those
 * is overridden here rather than by editing the shared component — 20+ other
 * screens depend on that base.
 */
const TAG = 'w-fit items-center border-0 px-2.5 py-1 text-xs leading-4 font-normal whitespace-nowrap';

/**
 * Status and intent tags use Figma's 16px radius; priority bands use 8px.
 * The 8px is written out rather than as `rounded-lg`: this project sets
 * `--radius` to 0.625rem, so `rounded-lg` resolves to 10px, not 8px.
 */
const PILL = `${TAG} rounded-2xl`;
const CHIP = `${TAG} rounded-[8px]`;

/** Figma grey/100 (#f4f4f6) on grey/800 (#7c828d) — the neutral tag fill. */
const GREY = 'bg-muted text-muted-foreground';

/**
 * `bg-green-100` (#dcfce7) rather than the `green-bg` token: that token
 * resolves to green-50 (#f0fdf4), but Figma's Shipped tag uses
 * Surface/Sucess-card (#dcfce7). Repointing `--color-green-bg` would recolour
 * every other green badge in the app, so the raw step is scoped here instead.
 */
const SHIPPED = 'bg-green-100 text-green-foreground';

/** Figma's orange status fill — shared by the gap-level and per-fix "Routed to tech" tags. */
const ORANGE = 'bg-orange-bg text-orange-foreground';

const STATUS_CLASSES: Record<GapStatus, string> = {
  open: GREY,
  routed_to_tech: ORANGE,
  test_passed: 'bg-amber-bg text-amber-foreground',
  shipped: SHIPPED,
  ignored: GREY,
  superseded: GREY,
};

/** P3/P4 sit on Figma grey/200 (#edeff2) — a step darker than the status grey. */
const BAND_CLASSES: Record<GapPriorityBand, string> = {
  P1: 'bg-red-bg text-red-foreground',
  P2: 'bg-amber-bg text-amber-foreground',
  P3: 'bg-gray-200 text-muted-foreground',
  P4: 'bg-gray-200 text-muted-foreground',
};

const SEVERITY_CLASSES: Record<GapSeverityLevel, string> = {
  critical: 'bg-red-bg text-red-foreground',
  normal: 'bg-amber-bg text-amber-foreground',
  weak: GREY,
};

const ACTION_CLASSES: Record<GapActionType, string> = {
  ack: 'bg-blue-bg text-blue-foreground',
  dispute: 'bg-red-bg text-red-foreground',
  mark_fixed: 'bg-emerald-bg text-emerald-foreground',
  note: GREY,
  route_tech: ORANGE,
  unroute_tech: GREY,
  reopen: GREY,
  fix_resolve: 'bg-emerald-bg text-emerald-foreground',
  fix_ignore: GREY,
  fix_route_tech: ORANGE,
  fix_reopen: GREY,
  fix_patch_approve: SHIPPED,
  fix_patch_reject: GREY,
  // System-only (see `GapActionType` in `types.ts`); the Activity tab never
  // renders these through `ActionTypeBadge`, but the map must stay exhaustive.
  test_passed: 'bg-amber-bg text-amber-foreground',
  test_failed: GREY,
  pass_revoked: GREY,
};

/**
 * Fix-card state chip fills — Resolved reuses the same Surface/Sucess-card
 * green as the gap-level Shipped tag, Ignored is the neutral grey, and
 * Routed to tech reuses the gap-level status's orange (both name the same
 * outcome, just at different granularity).
 */
const FIX_STATUS_CLASSES: Record<Exclude<GapFixStatus, 'open'>, string> = {
  resolved: SHIPPED,
  ignored: GREY,
  routed_to_tech: ORANGE,
};

/**
 * Figma's Tags variant that carries a leading icon (Tickets list, node
 * 4848:8801): a 12px glyph 6px from the label. `Badge`'s base ships `gap-1`
 * and `[&>svg]:size-4`, so both are overridden.
 */
const ICON_PILL = `${PILL} gap-1.5 [&>svg]:size-3`;

/** Test-result fills: the success green of Shipped, red/50 for a failure, grey otherwise. */
const TICKET_TEST_RESULT_CLASSES: Record<TicketTestResult, string> = {
  passed: SHIPPED,
  failed: 'bg-red-bg text-red-foreground',
  running: GREY,
  not_tested: GREY,
};

const TICKET_TEST_RESULT_ICONS: Record<TicketTestResult, React.ComponentType<{ className?: string; 'aria-hidden'?: 'true' }> | null> = {
  passed: Check,
  failed: X,
  running: Loader2,
  not_tested: null,
};

const UNKNOWN = GREY;

interface StatusBadgeProps {
  status: GapStatus | null;
  rawStatus: string;
  className?: string;
}

export function StatusBadge({ status, rawStatus, className }: StatusBadgeProps) {
  return (
    <Badge
      variant='outline'
      className={cn(PILL, status ? STATUS_CLASSES[status] : UNKNOWN, className)}
    >
      {formatStatusLabel(status, rawStatus)}
    </Badge>
  );
}

interface PriorityBandBadgeProps {
  band: GapPriorityBand | null;
  rawBand: string | null;
  className?: string;
}

/** The band alone — the tooltip that explains it lives in the column cell. */
export function PriorityBandBadge({ band, rawBand, className }: PriorityBandBadgeProps) {
  return (
    <Badge
      variant='outline'
      className={cn(CHIP, band ? BAND_CLASSES[band] : UNKNOWN, className)}
    >
      {band ?? rawBand ?? 'Unranked'}
    </Badge>
  );
}

interface SeverityBadgeProps {
  level: GapSeverityLevel | null;
  rawLevel: string | null;
  className?: string;
}

export function SeverityBadge({ level, rawLevel, className }: SeverityBadgeProps) {
  return (
    <Badge
      variant='outline'
      className={cn(CHIP, level ? SEVERITY_CLASSES[level] : UNKNOWN, className)}
    >
      {formatSeverityLabel(level, rawLevel)}
    </Badge>
  );
}

interface RungBadgeProps {
  rung: LadderRung | null;
  className?: string;
}

export function RungBadge({ rung, className }: RungBadgeProps) {
  if (!rung) {
    return <span className='text-xs text-muted-foreground'>—</span>;
  }
  return (
    <Badge variant='outline' className={cn(PILL, GREY, className)}>
      <span className='tabular-nums opacity-60'>{rungPosition(rung)}</span>
      {formatRungLabel(rung)}
    </Badge>
  );
}

interface ActionTypeBadgeProps {
  type: GapActionType | null;
  rawType: string;
  className?: string;
}

export function ActionTypeBadge({ type, rawType, className }: ActionTypeBadgeProps) {
  return (
    <Badge
      variant='outline'
      className={cn(PILL, type ? ACTION_CLASSES[type] : UNKNOWN, className)}
    >
      {formatActionLabel(type, rawType)}
    </Badge>
  );
}

interface FixStatusBadgeProps {
  status: GapFixStatus | null;
  className?: string;
}

/**
 * A suggested-fix card's state chip. Renders nothing for `open`/null — the
 * design shows no chip until a fix has actually been resolved, ignored or
 * routed. The status is applied in the same request that records the action,
 * so this chip is always the fix's current state.
 */
export function FixStatusBadge({ status, className }: FixStatusBadgeProps) {
  const label = formatFixStatusLabel(status);
  if (!status || status === 'open' || !label) return null;
  const Icon = status === 'resolved' ? Check : status === 'ignored' ? X : null;
  return (
    <Badge variant='outline' className={cn(PILL, FIX_STATUS_CLASSES[status], className)}>
      {Icon && <Icon className='h-3 w-3' />}
      {label}
    </Badge>
  );
}

/** Data-correction pill fills: amber while it awaits a decision, then the Resolved / Ignored fills. */
const WORLD_PATCH_STATUS_CLASSES: Record<GapWorldPatchStatus, string> = {
  proposed: 'bg-amber-bg text-amber-foreground',
  approved: SHIPPED,
  rejected: GREY,
};

const WORLD_PATCH_STATUS_ICONS: Record<GapWorldPatchStatus, typeof Check | null> = {
  proposed: null,
  approved: Check,
  rejected: X,
};

interface WorldPatchStatusBadgeProps {
  status: GapWorldPatchStatus;
  className?: string;
}

/** A fix's data-correction state, in the fix card's footer. */
export function WorldPatchStatusBadge({ status, className }: WorldPatchStatusBadgeProps) {
  const Icon = WORLD_PATCH_STATUS_ICONS[status];
  return (
    <Badge variant='outline' className={cn(ICON_PILL, WORLD_PATCH_STATUS_CLASSES[status], className)}>
      {Icon ? <Icon aria-hidden='true' /> : null}
      {formatWorldPatchStatus(status)}
    </Badge>
  );
}

interface TicketTestResultBadgeProps {
  result: TicketTestResult;
  className?: string;
}

/**
 * A ticket's result in the gap's newest test run, in the Tickets list's tag style
 * (Figma nodes 4848:8830, 4848:8832).
 */
export function TicketTestResultBadge({ result, className }: TicketTestResultBadgeProps) {
  const Icon = TICKET_TEST_RESULT_ICONS[result];
  return (
    <Badge variant='outline' className={cn(ICON_PILL, TICKET_TEST_RESULT_CLASSES[result], className)}>
      {Icon ? <Icon aria-hidden='true' className={result === 'running' ? 'animate-spin' : undefined} /> : null}
      {formatTicketTestResult(result)}
    </Badge>
  );
}

/**
 * Shown on an action-history row that only exists in the local cache — the
 * mutation hasn't resolved yet. Distinct from the deleted `PendingBadge`:
 * that one meant "waiting on the next steward pass" (a concept that no
 * longer exists); this means "not yet confirmed by the server", and rolls
 * back to nothing if the request fails.
 */
export function InFlightActionBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant='outline'
      className={cn(PILL, 'bg-amber-bg text-amber-foreground', className)}
      title='Not yet confirmed by the server — this row disappears if the action fails'
    >
      In flight
    </Badge>
  );
}

/**
 * Shown when no trace behind a gap has a recorded human reply, so the
 * verdict rests on trace evidence alone.
 */
export function NoOutcomeEvidenceBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant='outline'
      className={cn(PILL, UNKNOWN, className)}
      title='No human reply has been recorded for any trace behind this gap — it was judged on trace evidence only'
    >
      No outcome evidence
    </Badge>
  );
}

/**
 * The neutral grey pill used for intent chips and their `+N` overflow — the
 * same Figma Tags treatment as a grey status, so it lives here rather than
 * being re-derived in the cell renderer.
 */
export function IntentBadge({ children, ...props }: React.ComponentProps<typeof Badge>) {
  return (
    <Badge variant='outline' className={cn(PILL, GREY)} {...props}>
      {children}
    </Badge>
  );
}

interface BucketBadgeProps {
  bucket: string | null;
  className?: string;
}

export function BucketBadge({ bucket, className }: BucketBadgeProps) {
  if (!bucket) {
    return <span className='text-xs text-muted-foreground'>—</span>;
  }
  return (
    <Badge variant='outline' className={cn(PILL, GREY, className)}>
      {bucket}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Testing rail — gap test runs (Figma "Testing" section, nodes 4848:10134/
// 9852/10041/10227)
// ---------------------------------------------------------------------------

/** Figma text/Secondary (#5f656f) — the rail's score/count labels, distinct from `muted-foreground`. */
const RAIL_MUTED = 'text-gray-900';

interface TestRunStatusPillProps {
  status: GapRunSetStatus | null;
  /** Overrides the status's own label (Run history's "Invalidated"); the colour still follows `status`. */
  label?: string;
  className?: string;
}

/**
 * The Testing section's score-row pill. `passed`/`failed` reuse the Tags
 * component's green/red treatment; `running`/`cancelled`/`stale` are a plain
 * grey rounded-full pill — Figma draws them as a different component, not a
 * Tags variant.
 */
export function TestRunStatusPill({ status, label: labelOverride, className }: TestRunStatusPillProps) {
  const label = labelOverride ?? formatRunSetStatusLabel(status);
  if (status === 'passed') {
    return (
      <Badge variant='outline' className={cn(ICON_PILL, SHIPPED, className)}>
        <Check aria-hidden='true' />
        {label}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant='outline' className={cn(ICON_PILL, 'bg-red-bg text-red-foreground', className)}>
        <AlertCircle aria-hidden='true' />
        {label}
      </Badge>
    );
  }
  if (status === 'running') {
    return (
      <Badge
        variant='outline'
        className={cn(TAG, 'gap-2 rounded-full bg-muted', RAIL_MUTED, className)}
      >
        <Loader2 aria-hidden='true' className='size-3 animate-spin' />
        {label}
      </Badge>
    );
  }
  // cancelled, stale, or an unrecognised status — same grey pill, no icon.
  return (
    <Badge variant='outline' className={cn(TAG, 'rounded-full bg-muted', RAIL_MUTED, className)}>
      {label}
    </Badge>
  );
}

interface RunRowStatusChipProps {
  status: GapRunRowStatus | null;
  className?: string;
}

/** The Tickets section row's status chip — Queued / Running / Skipped / Error / Done. */
export function RunRowStatusChip({ status, className }: RunRowStatusChipProps) {
  const label = formatRunRowStatusLabel(status);
  if (status === 'running') {
    return (
      <Badge variant='outline' className={cn(CHIP, GREY, 'gap-1', className)}>
        <Loader2 aria-hidden='true' className='size-3 animate-spin' />
        {label}
      </Badge>
    );
  }
  if (status === 'error') {
    return (
      <Badge variant='outline' className={cn(CHIP, 'bg-red-bg text-red-foreground', className)}>
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant='outline' className={cn(CHIP, GREY, className)}>
      {label}
    </Badge>
  );
}

interface RunVerdictChipProps {
  verdict: GapRunVerdict | null;
  className?: string;
}

const RUN_VERDICT_CLASSES: Record<GapRunVerdict, string> = {
  pass: SHIPPED,
  pass_with_flag: 'bg-amber-bg text-amber-foreground',
  fail: 'bg-red-bg text-red-foreground',
  unchanged_or_better: SHIPPED,
  regressed: 'bg-red-bg text-red-foreground',
};

/** The verdict chip beside a `done` row's status chip — absent until the row is judged. */
export function RunVerdictChip({ verdict, className }: RunVerdictChipProps) {
  const label = formatRunVerdictLabel(verdict);
  if (!verdict || !label) return null;
  return (
    <Badge variant='outline' className={cn(CHIP, RUN_VERDICT_CLASSES[verdict], className)}>
      {label}
    </Badge>
  );
}
